import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  getBlogsForPublicAPI,
  getBlogsForPublicBySlugAPI,
  getUniqueBlogTagsAPI,
} from "@/apis/blogs";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Linkedin,
  Facebook,
  Share2,
  BookmarkIcon,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import moment from "moment";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { XLogo } from "@/components/features/landing/XLogo";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { BlueSkyLogo } from "@/components/features/landing/BlueSkyLogo";

export default function PublicBlogDetailPage() {
  const router = useRouter();
  const { slug } = router.query;
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailSubscribe, setEmailSubscribe] = useState("");
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showTags, setShowTags] = useState(true); // Control tag visibility on mobile

  useEffect(() => {
    if (!slug) return;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await getBlogsForPublicBySlugAPI(slug);

        if (response.success) {
          // Only show published blogs
          if (response.data.status !== "Published") {
            router.push("/blogs");
            return;
          }
          setBlog(response.data);

          // Check if this blog is bookmarked in localStorage
          checkIfBookmarked(response.data._id);

          // Fetch related blogs based on tags
          if (response.data.tags && response.data.tags.length > 0) {
            fetchRelatedBlogs(response.data.tags, response.data._id);
          }
        } else {
          setError(response.message || "Failed to fetch blog details");
          toast.error(response.message || "Failed to fetch blog details");
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Something went wrong while fetching blog details");
        toast.error("Something went wrong while fetching blog details");
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();

    // Handle window resize for tag display
    const handleResize = () => {
      setShowTags(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Set initial state

    return () => window.removeEventListener("resize", handleResize);
  }, [slug, router]);

  // Check if blog is bookmarked
  const checkIfBookmarked = (blogId) => {
    if (typeof window === "undefined") return;

    const bookmarks = JSON.parse(localStorage.getItem("blogBookmarks") || "[]");
    setIsBookmarked(bookmarks.includes(blogId));
  };

  // Toggle bookmark status
  const toggleBookmark = () => {
    if (!blog) return;

    const bookmarks = JSON.parse(localStorage.getItem("blogBookmarks") || "[]");

    if (isBookmarked) {
      // Remove from bookmarks
      const updatedBookmarks = bookmarks.filter((id) => id !== blog._id);
      localStorage.setItem("blogBookmarks", JSON.stringify(updatedBookmarks));
      setIsBookmarked(false);
      toast.success("Removed from bookmarks");
    } else {
      // Add to bookmarks
      bookmarks.push(blog._id);
      localStorage.setItem("blogBookmarks", JSON.stringify(bookmarks));
      setIsBookmarked(true);
      toast.success("Added to bookmarks");
    }
  };

  // Fetch related blogs based on tags
  const fetchRelatedBlogs = async (tags, currentBlogId) => {
    try {
      // Assume we're reusing the same API for fetching blogs
      const params = {
        limit: 3,
        status: "Published",
        tag: tags[0], // Use the first tag for related content
        isOnFTA: true,
      };

      const response = await getBlogsForPublicAPI(params);

      if (response.success) {
        // Filter out the current blog
        const filteredBlogs = response.data.filter(
          (blog) => blog._id !== currentBlogId
        );
        setRelatedBlogs(filteredBlogs.slice(0, 3)); // Limit to 3 related blogs
      }
    } catch (error) {
      console.error("Error fetching related blogs:", error);
    }
  };

  // Function to estimate read time based on content length
  const getReadTime = (content) => {
    if (!content) return "2 min read";
    // Strip HTML tags
    const text = content.replace(/<[^>]*>?/gm, "");
    // Average reading speed: 200 words per minute
    const wordCount = text.split(/\s+/).length;
    const readTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));
    return `${readTimeMinutes} min read`;
  };

  // Format date
  const formatDate = (date) => {
    return moment(date).format("MMMM D, YYYY");
  };

  // Get author name
  const getAuthorName = () => {
    if (!blog?.createdBy) return "FTA Team";
    const { firstName, lastName } = blog.createdBy;
    return `${firstName || ""} ${lastName || ""}`.trim() || "FTA Team";
  };

  // Get image path
  const getImagePath = (path) => {
    if (!path) return "/placeholder-blog.jpg"; // Fallback placeholder
    // Check if the path is a full URL
    if (path.startsWith("http")) return path;

    // Check if it's a server-side path
    if (path.startsWith("/Users/") || path.startsWith("/var/")) {
      // Extract just the filename from the path
      const fileName = path.split("/").pop();
      // Return a placeholder or a proper URL to your image API
      return `/api/images/${fileName}`;
    }

    // Otherwise, return the path as is
    return path;
  };

  // Toggle tags visibility on mobile
  const toggleTagsVisibility = () => {
    setShowTags(!showTags);
  };

  // Handle newsletter subscription
  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!emailSubscribe.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    // Here you would typically call an API to subscribe the user
    toast.success("Thank you for subscribing to our newsletter!");
    setEmailSubscribe("");
  };

  // Get current URL for sharing
  const getCurrentUrl = () => {
    return typeof window !== "undefined" ? window.location.href : "";
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600 font-medium">
              Loading article...
            </span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !blog) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center bg-white p-8 rounded-xl shadow-sm max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              <AlertCircle className="w-full h-full" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Blog Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The blog post you&apos;re looking for doesn&apos;t exist or is no
              longer available.
            </p>
            <Link href="/blogs">
              <Button className="px-6">Return to Blog Home</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Back Link and Bookmark */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <Link
              href="/blogs"
              className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span>Back to Blog</span>
            </Link>

            <Button
              variant="ghost"
              onClick={toggleBookmark}
              className={`p-2 ${
                isBookmarked ? "text-blue-600" : "text-gray-500"
              }`}
              title={isBookmarked ? "Remove bookmark" : "Bookmark this article"}
            >
              <BookmarkIcon
                className={`w-5 h-5 ${isBookmarked ? "fill-blue-600" : ""}`}
              />
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 bg-white shadow-sm mt-1">
          {/* Featured Image */}
          {blog.infographic && (
            <div className="pt-6 pb-4">
              <div className="relative w-full h-64 md:h-[400px] rounded-xl overflow-hidden border border-gray-100">
                <Image
                  src={getImagePath(blog.infographic)}
                  alt={blog.title}
                  fill
                  className="object-contain"
                  priority
                />
              </div>
            </div>
          )}

          {/* Blog Content */}
          <div className="py-6">
            {/* Tags */}
            <div className="mb-4 md:block">
              <div className="flex items-center mb-2 md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm text-gray-600 p-0 hover:bg-transparent"
                  onClick={toggleTagsVisibility}
                >
                  {showTags ? "Hide tags" : "Show tags"}
                  <ChevronRight
                    className={`ml-1 w-4 h-4 transition-transform ${
                      showTags ? "rotate-90" : ""
                    }`}
                  />
                </Button>
              </div>

              {showTags && blog.tags && blog.tags.length > 0 && (
                <div className="flex flex-wrap">
                  {blog.tags.map((tag, i) => (
                    <Badge
                      key={i}
                      variant="primary"
                      size="md"
                      className="mr-2 mb-2"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {blog.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center text-gray-600 mb-8 gap-x-6 gap-y-2 pb-6 border-b border-gray-100">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span className="font-medium">{getAuthorName()}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{formatDate(blog.createdAt)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{getReadTime(blog.content)}</span>
              </div>
            </div>

            {/* Blog Content */}
            <div
              className="text-gray-700 prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-p:text-gray-700 prose-p:my-4 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg break-words"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Tags (Bottom) - Helpful for SEO and user navigation */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <h3 className="text-sm uppercase text-gray-500 font-medium mb-3">
                  Topics
                </h3>
                <div className="flex flex-wrap">
                  {blog.tags.map((tag, i) => (
                    <Link key={i} href={`/blogs?tag=${tag}`} passHref>
                      <Badge
                        variant="secondary"
                        size="lg"
                        className="mr-2 mb-2 cursor-pointer hover:bg-gray-200"
                      >
                        {tag}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Share Section */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Share2 className="w-5 h-5 mr-2 text-gray-500" />
                Share this article
              </h3>
              <div className="flex flex-wrap gap-3">
                <Link
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                    getCurrentUrl()
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="h-10 w-10 p-0 flex items-center justify-center"
                    title="Share on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </Button>
                </Link>
                <Link
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    getCurrentUrl()
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="h-10 w-10 p-0 flex items-center justify-center"
                    title="Share on Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </Button>
                </Link>
                <Link
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                    getCurrentUrl()
                  )}&text=${encodeURIComponent(blog.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="h-10 w-10 p-0 flex items-center justify-center"
                    title="Share on X"
                  >
                    <div className="flex items-center justify-center">
                      <XLogo className="w-4 h-4" />
                    </div>
                  </Button>
                </Link>
                <Link
                  href={`https://bsky.app/intent/compose?text=${encodeURIComponent(
                    `${blog.title}\n\n${getCurrentUrl()}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="h-10 w-10 p-0 flex items-center justify-center"
                    title="Share on BlueSky"
                  >
                    <div className="flex items-center justify-center">
                      <BlueSkyLogo className="w-5 h-5" />
                    </div>
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="flex items-center"
                  onClick={() => {
                    navigator.clipboard.writeText(getCurrentUrl());
                    toast.success("Link copied to clipboard");
                  }}
                  title="Copy link"
                >
                  <span>Copy Link</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          {relatedBlogs.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Related Articles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog._id}
                    href={`/blogs/${relatedBlog.slug}`}
                    className="group"
                  >
                    <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100 h-full transition-shadow hover:shadow-md">
                      {/* Image */}
                      <div className="relative h-40 w-full overflow-hidden">
                        <Image
                          src={getImagePath(relatedBlog.infographic)}
                          alt={relatedBlog.title}
                          fill
                          className="object-contain transition-transform group-hover:scale-105"
                        />
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {relatedBlog.title}
                        </h4>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          <span>{getReadTime(relatedBlog.content)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to top button */}
          <div className="flex justify-center mt-12">
            <Button
              variant="ghost"
              className="text-gray-500 hover:text-blue-600 flex items-center"
              onClick={scrollToTop}
            >
              Back to top
            </Button>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 py-16 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="mb-8 max-w-2xl mx-auto text-blue-50">
            Subscribe to our newsletter for the latest career insights, job
            search tips, and professional development strategies.
          </p>

          <form
            onSubmit={handleSubscribe}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-white text-gray-900 shadow-sm"
              value={emailSubscribe}
              onChange={(e) => setEmailSubscribe(e.target.value)}
              required
              aria-label="Email address"
            />
            <Button
              type="submit"
              className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 shadow-sm px-5"
            >
              Subscribe
            </Button>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
